import { RUB_PER_UNIT } from "./currency";

const SYMBOL_TO_CODE: Record<string, string> = { "₽": "RUB", "₴": "UAH", "₸": "KZT", "$": "USD", "€": "EUR" };
const CODES = Object.keys(RUB_PER_UNIT);

export type ParsedMoney = { amount: number; currency: string | null };

/** Reads a backend amount that may be a number, `null`, or a string with a currency marker (`"1 250,50 ₽"`, `"USD 100"`). */
export function parseMoneyValue(value: unknown): ParsedMoney | null {
  if (value === null || value === undefined) return { amount: 0, currency: null };
  if (typeof value === "number") return Number.isFinite(value) ? { amount: value, currency: null } : null;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return { amount: 0, currency: null };
  const upper = text.toUpperCase();
  const code = CODES.find((candidate) => new RegExp(`(^|[^A-Z])${candidate}($|[^A-Z])`).test(upper))
    ?? Object.entries(SYMBOL_TO_CODE).find(([symbol]) => text.includes(symbol))?.[1]
    ?? (/(^|\d|\s)Br\b/.test(text) ? "BYN" : null);
  const digits = text.replace(/[^\d.,-]/g, "");
  if (!/\d/.test(digits)) return null;
  const lastSeparator = Math.max(digits.lastIndexOf("."), digits.lastIndexOf(","));
  const fraction = lastSeparator >= 0 ? digits.slice(lastSeparator + 1) : "";
  // Three digits after a comma are a thousands group; a lone dot stays a decimal point.
  const separator = lastSeparator >= 0 ? digits[lastSeparator] : "";
  const decimal = lastSeparator >= 0 && (fraction.length !== 3 || (separator === "." && digits.split(".").length === 2 && !digits.includes(",")));
  const normalized = decimal
    ? `${digits.slice(0, lastSeparator).replace(/[.,]/g, "")}.${fraction}`
    : digits.replace(/[.,]/g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? { amount, currency: code } : null;
}

/** Amount expressed in `target` currency; converts through fixed RUB rates when the value carries a different currency marker. */
export function moneyInCurrency(parsed: ParsedMoney, target: string): number {
  const source = parsed.currency;
  if (!source || source === target.toUpperCase()) return parsed.amount;
  const from = RUB_PER_UNIT[source];
  const to = RUB_PER_UNIT[target.toUpperCase()];
  return from && to ? (parsed.amount * from) / to : parsed.amount;
}
